import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { 
  Shield, Check, FileText, PlayCircle, ArrowRight, 
  BookOpen, CheckCircle, Clock, ChevronDown, ChevronUp,
  Lock, Sparkles, Volume2, VolumeX, Play, X, Eye, Users, Search, Camera, Key, Wifi, Mail, Mic
} from 'lucide-react';
import { API_URL } from '../config';
import { trackViewContent, trackInitiateCheckout } from '../utils/metaPixel';
import BlueprintFlipbook from '../components/BlueprintFlipbook';
import DigitalSafetyScore from '../components/DigitalSafetyScore';
import '../styles/Blueprint.css';
import '../styles/BlueprintFlipbook.css';

// Benefit chip data with explanations
const BENEFIT_CHIPS_DATA = [
  {
    id: 'ad-tracking',
    label: 'Reduce invasive tracking',
    icon: Eye,
    explanation: 'Ad networks build profiles from what you watch, click, and browse. Reducing tracking means less of your behaviour gets packaged and sold.',
    protects: 'your browsing habits'
  },
  {
    id: 'platforms',
    label: 'Share less with platforms',
    icon: Users,
    explanation: 'Platforms build profiles about you using the signals you leave behind. Sharing less data keeps that profile thinner.',
    protects: 'your behaviour profile'
  },
  {
    id: 'findability',
    label: 'Be harder to find online',
    icon: Search,
    explanation: 'If someone can find your profiles using your phone number or email, it becomes easier to connect the dots across your digital life.',
    protects: 'identity links between accounts'
  },
  {
    id: 'photos',
    label: 'Keep your photos more private',
    icon: Camera,
    explanation: 'Public photos can easily be copied, reused, or reposted. Better privacy settings help limit how widely they spread.',
    protects: 'your image and likeness'
  },
  {
    id: 'app-access',
    label: 'Remove old app access',
    icon: Key,
    explanation: 'Old "Sign in with Google/Apple/Facebook" connections create extra doors into your accounts. Removing unused ones closes those doors.',
    protects: 'your accounts and logins'
  },
  {
    id: 'wifi',
    label: 'Stay safer on public Wi-Fi',
    icon: Wifi,
    explanation: 'On Wi-Fi networks you don\'t control, your data can be exposed. Using the right protections helps secure banking, email, and work logins.',
    protects: 'sensitive logins'
  },
  {
    id: 'inbox',
    label: 'Strengthen your inbox',
    icon: Mail,
    explanation: 'Your inbox is the control centre for your digital life. Password resets and identity confirmations usually go through email.',
    protects: 'account recovery'
  },
  {
    id: 'microphone',
    label: 'Limit microphone access',
    icon: Mic,
    explanation: 'Many apps request microphone access and it often stays enabled long after you forget about it.',
    protects: 'private conversations'
  }
];

// BenefitChipsWithExplanations Component
const BenefitChipsWithExplanations = () => {
  const [activeChip, setActiveChip] = useState(null);
  const [isAutoPlaying, setIsAutoPlaying] = useState(false);
  const [hasCompletedSequence, setHasCompletedSequence] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef(null);
  const autoPlayTimerRef = useRef(null);
  
  // Intersection Observer to start autoplay when visible
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );
    
    if (containerRef.current) {
      observer.observe(containerRef.current);
    }
    
    return () => observer.disconnect();
  }, [isVisible]);
  
  // Start autoplay sequence when visible
  useEffect(() => {
    if (isVisible && !hasCompletedSequence && !isAutoPlaying) {
      // Delay start slightly for smooth entry
      const startTimer = setTimeout(() => {
        setIsAutoPlaying(true);
        setActiveChip(0);
      }, 500);
      
      return () => clearTimeout(startTimer);
    }
  }, [isVisible, hasCompletedSequence, isAutoPlaying]);
  
  // Autoplay timer - advance to next chip
  useEffect(() => {
    if (isAutoPlaying && activeChip !== null) {
      autoPlayTimerRef.current = setTimeout(() => {
        const nextChip = activeChip + 1;
        if (nextChip < BENEFIT_CHIPS_DATA.length) {
          setActiveChip(nextChip);
        } else {
          // Sequence complete
          setIsAutoPlaying(false);
          setHasCompletedSequence(true);
        }
      }, 2800); // 2.8s per chip
      
      return () => {
        if (autoPlayTimerRef.current) {
          clearTimeout(autoPlayTimerRef.current);
        }
      };
    }
  }, [isAutoPlaying, activeChip]);
  
  // Handle manual chip click
  const handleChipClick = (index) => {
    // Stop autoplay if running
    if (isAutoPlaying) {
      setIsAutoPlaying(false);
      setHasCompletedSequence(true);
      if (autoPlayTimerRef.current) {
        clearTimeout(autoPlayTimerRef.current);
      }
    }
    setActiveChip(index);
  };
  
  // Handle keyboard navigation
  const handleKeyDown = (e, index) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleChipClick(index);
    }
  };
  
  const activeData = activeChip !== null ? BENEFIT_CHIPS_DATA[activeChip] : null;
  const IconComponent = activeData?.icon;
  
  return (
    <div className="benefit-chips-container" ref={containerRef}>
      {/* Chip Grid */}
      <div className="blueprint-benefit-chips" id="benefit-chips">
        {BENEFIT_CHIPS_DATA.map((chip, index) => (
          <button
            key={chip.id}
            className={`benefit-chip ${activeChip === index ? 'active' : ''} ${hasCompletedSequence ? 'interactive' : ''}`}
            onClick={() => handleChipClick(index)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            tabIndex={0}
            aria-pressed={activeChip === index}
            data-testid={`benefit-chip-${chip.id}`}
          >
            {chip.label}
          </button>
        ))}
      </div>
      
      {/* Explanation Card */}
      <div className={`benefit-explanation-card ${activeChip !== null ? 'visible' : ''}`}>
        {activeData && (
          <div className="explanation-card-content" key={activeChip}>
            <div className="explanation-header">
              {IconComponent && <IconComponent size={20} className="explanation-icon" />}
              <span className="explanation-label">Why this matters</span>
            </div>
            <p className="explanation-text">{activeData.explanation}</p>
            <div className="explanation-protects">
              <Shield size={14} />
              <span>Protects: <strong>{activeData.protects}</strong></span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Brand assets
const LOGO_URL = "https://customer-assets.emergentagent.com/job_express-checkout-9/artifacts/65fw8myi_Variant%201_Full%20Logo_Vector%20copy.png";
const SHIELD_URL = "https://customer-assets.emergentagent.com/job_0e5aee2f-27de-4cc5-bc22-1ba27f01672b/artifacts/f8dzsnko_Variant%201_Shield%20Only_Vector.png";

// Video thumbnail placeholder
const VIDEO_THUMBNAIL = "https://customer-assets.emergentagent.com/job_edupages-2/artifacts/k4f5b9sc_image.png";

// Vimeo embed URL
const VIMEO_VIDEO_ID = "1170557010";

// SEO structured data
const blueprintStructuredData = {
  "@context": "https://schema.org",
  "@type": "Product",
  "name": "Course 1 PDF Blueprint",
  "description": "A calm, step-by-step PDF Blueprint that turns security into a simple checklist you can follow at your own pace.",
  "brand": {
    "@type": "Brand",
    "name": "All Secured"
  },
  "offers": {
    "@type": "Offer",
    "price": "28.00",
    "priceCurrency": "AUD",
    "availability": "https://schema.org/InStock"
  }
};

const BlueprintLanding = () => {
  const [searchParams] = useSearchParams();
  const [selectedOption, setSelectedOption] = useState('pdf');
  const [loading, setLoading] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [showStickyBar, setShowStickyBar] = useState(false);
  const [videoMuted, setVideoMuted] = useState(true);
  const [showUnmutePrompt, setShowUnmutePrompt] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [visibleSections, setVisibleSections] = useState({});
  const [tileVideoMuted, setTileVideoMuted] = useState(true);
  const tileVideoRef = useRef(null);
  
  const videoRef = useRef(null);
  const sectionsRef = useRef({});

  // Track ViewContent on page load and scroll to top
  useEffect(() => {
    // Force scroll to top immediately on mount
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    // Also set after a small delay to handle any race conditions
    const scrollTimer = setTimeout(() => {
      window.scrollTo(0, 0);
    }, 50);
    
    trackViewContent({
      content_name: 'Course 1 PDF Blueprint',
      content_id: 'blueprint-pdf',
      content_type: 'product',
      value: 28.00,
      currency: 'AUD'
    });
    
    return () => clearTimeout(scrollTimer);

    // Preserve UTM params
    const utmParams = {};
    ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'].forEach(param => {
      const value = searchParams.get(param);
      if (value) utmParams[param] = value;
    });
    if (Object.keys(utmParams).length > 0) {
      sessionStorage.setItem('blueprint_utm', JSON.stringify(utmParams));
    }

    // Show unmute prompt after delay
    const timer = setTimeout(() => setShowUnmutePrompt(true), 2500);
    return () => clearTimeout(timer);
  }, [searchParams]);

  // Scroll and intersection observer for animations
  useEffect(() => {
    const handleScroll = () => {
      setShowStickyBar(window.scrollY > 500);
    };
    window.addEventListener('scroll', handleScroll);

    // Intersection observer for section animations
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setVisibleSections(prev => ({ ...prev, [entry.target.id]: true }));
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    // Observe sections (excluding benefit-chips which is handled separately)
    document.querySelectorAll('[data-animate]:not(#benefit-chips)').forEach(el => observer.observe(el));

    return () => {
      window.removeEventListener('scroll', handleScroll);
      observer.disconnect();
    };
  }, []);

  const handleCheckout = async (productType) => {
    setLoading(true);
    
    const productData = productType === 'pdf' 
      ? { content_name: 'Course 1 PDF Blueprint', content_id: 'blueprint-pdf', value: 28.00 }
      : { content_name: 'Course 1 Guided Videos + Blueprint', content_id: 'guided-videos', value: 88.00 };
    
    trackInitiateCheckout({
      ...productData,
      currency: 'AUD'
    });

    try {
      let utmParams = {};
      try {
        utmParams = JSON.parse(sessionStorage.getItem('blueprint_utm') || '{}');
      } catch (e) {}

      const response = await axios.post(`${API_URL}/api/blueprint/checkout`, {
        product_type: productType,
        utm_params: utmParams
      });

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error('Checkout error:', error);
      alert('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleUnmute = (e) => {
    e.stopPropagation();
    setVideoMuted(false);
    setShowUnmutePrompt(false);
  };

  const openVideoModal = () => {
    setShowVideoModal(true);
    document.body.style.overflow = 'hidden';
  };

  const closeVideoModal = () => {
    setShowVideoModal(false);
    document.body.style.overflow = '';
  };

  const faqs = [
    {
      q: "How do I receive it?",
      a: "After checkout you'll see a thank-you page, and the Blueprint will be delivered to your email instantly."
    },
    {
      q: "Is this technical?",
      a: "No — it's written in plain language and focused on practical steps."
    },
    {
      q: "How long does it take?",
      a: "Most people can make meaningful improvements in one sitting, then come back later to finish the rest."
    },
    {
      q: "What if I want the full course later?",
      a: "You can upgrade anytime — we'll credit the $28 Blueprint purchase toward the $88 full course."
    },
    {
      q: "Is this the same as the full course?",
      a: "The Blueprint is the condensed action plan. The full course adds guided videos and extra walkthrough support."
    }
  ];

  return (
    <div className="blueprint-page">
      {/* Animated Background */}
      <div className="bp-bg-gradient"></div>
      <div className="bp-bg-mesh"></div>
      <div className="bp-bg-honeycomb"></div>

      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(blueprintStructuredData)}
      </script>

      {/* Header with premium logo treatment */}
      <header className="blueprint-header">
        <Link to="/" className="blueprint-logo-link">
          <div className="blueprint-logo-pill">
            <img src={LOGO_URL} alt="All Secured" />
          </div>
        </Link>
      </header>

      {/* Hero Section */}
      <section className="blueprint-hero">
        <div className="blueprint-hero-content">
          <div className="blueprint-hero-text">
            {/* Headline with shimmer effect */}
            <h1 className="blueprint-headline">
              <span className="headline-shimmer">
                Your Digital Life Shouldn't Be Easy to Read
              </span>
            </h1>
            <p className="blueprint-subheadline">
              Reduce what people can access, apps can collect, and platforms can quietly learn about you.
            </p>

            {/* Premium Benefit Chips with Interactive Explanations */}
            <BenefitChipsWithExplanations />

            {/* Format Selector - Enhanced */}
            <div className="blueprint-format-selector">
              <div 
                className={`format-card ${selectedOption === 'pdf' ? 'selected' : ''}`}
                onClick={() => setSelectedOption('pdf')}
                role="button"
                tabIndex={0}
                aria-pressed={selectedOption === 'pdf'}
              >
                <div className="format-card-glow"></div>
                <div className="format-card-content">
                  <div className="format-card-header">
                    <FileText size={24} />
                    <div className="format-card-badge">
                      <Sparkles size={10} />
                      Recommended to start
                    </div>
                  </div>
                  <div className="format-card-badge-type">Self-guided</div>
                  <h3>PDF Blueprint</h3>
                  <p className="format-best-for">Best for people who want a simple checklist they can follow at their own pace.</p>
                  <p className="format-price">$28 <span>AUD</span></p>
                  <ul className="format-features">
                    <li><Check size={14} /> Follow one page per module</li>
                    <li><Check size={14} /> Clear "done-when" checkpoints so you know you're finished</li>
                    <li><Check size={14} /> Apply the steps at your own pace</li>
                  </ul>
                </div>
              </div>

              <div 
                className={`format-card ${selectedOption === 'videos' ? 'selected' : ''}`}
                onClick={() => setSelectedOption('videos')}
                role="button"
                tabIndex={0}
                aria-pressed={selectedOption === 'videos'}
              >
                <div className="format-card-glow"></div>
                <div className="format-card-content">
                  <div className="format-card-header">
                    <PlayCircle size={24} />
                  </div>
                  <div className="format-card-badge-type guided">Guided</div>
                  <h3>Guided Videos + Blueprint</h3>
                  <p className="format-best-for">Best for people who want to see every step on screen and follow along.</p>
                  <p className="format-price">$88 <span>AUD</span></p>
                  <p className="format-includes-note">Includes the full Blueprint ($28 value)</p>
                  <ul className="format-features">
                    <li><Check size={14} /> See the exact settings on real screens</li>
                    <li><Check size={14} /> Follow along step-by-step</li>
                    <li><Check size={14} /> Includes the full Blueprint</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Format clarifier - Above the fold explainer */}
            <div className="format-clarifier">
              <p><strong>PDF Blueprint</strong> = checklist + checkpoints (self-guided).</p>
              <p><strong>Guided Videos</strong> = we walk you through every screen step-by-step.</p>
            </div>

            {/* Primary CTA */}
            <button 
              className="blueprint-cta-primary"
              onClick={() => handleCheckout(selectedOption)}
              disabled={loading}
            >
              {loading ? (
                <span className="cta-loading">Processing...</span>
              ) : (
                <>
                  {selectedOption === 'pdf' 
                    ? 'Get the PDF Blueprint — $28'
                    : 'Get Guided Videos + Blueprint — $88'}
                  <ArrowRight size={20} />
                </>
              )}
            </button>

            {/* Reassurance microcopy under CTA */}
            <div className="cta-reassurance">
              <p>Instant PDF delivered via email</p>
              <p>One-time payment. No subscription.</p>
            </div>

            {/* Stripe security line */}
            <div className="stripe-security">
              <Lock size={14} />
              <span>Secure checkout with Stripe • SSL encrypted</span>
            </div>

            {/* Upgrade note */}
            <p className="blueprint-upgrade-note">
              <Sparkles size={14} /> Start with the PDF. Upgrade later to videos — your $28 is credited.
            </p>
          </div>

          {/* Dynamic Preview Area */}
          <div className="blueprint-hero-visual">
            <span className="preview-label">Preview</span>
            {selectedOption === 'pdf' ? (
              <div className="pdf-preview-container">
                <BlueprintFlipbook 
                  isActive={selectedOption === 'pdf'} 
                  onReady={() => console.log('Flipbook ready')}
                />
              </div>
            ) : (
              <div className="video-preview-container">
                <iframe
                  ref={tileVideoRef}
                  src={`https://player.vimeo.com/video/1166189270?h=17a7a06214&autoplay=1&loop=1&muted=${tileVideoMuted ? 1 : 0}&background=0&badge=0&autopause=0&player_id=tile-video&app_id=58479&texttrack=false`}
                  className="tile-video-iframe"
                  allow="autoplay; fullscreen; picture-in-picture"
                  allowFullScreen
                  title="Course preview video"
                />
                <button 
                  className={`video-unmute-overlay ${!tileVideoMuted ? 'hidden' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setTileVideoMuted(false);
                  }}
                  aria-label="Unmute video"
                >
                  {tileVideoMuted ? <VolumeX size={18} /> : <Volume2 size={18} />}
                  <span>Tap to unmute</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Digital Safety Score — Interactive Quiz Module */}
      <DigitalSafetyScore onCheckout={handleCheckout} />

      {/* Blueprint Anatomy Section - Premium Product Storytelling */}
      <section 
        className={`blueprint-section blueprint-anatomy ${visibleSections['whats-inside'] ? 'visible' : ''}`} 
        id="whats-inside" 
        data-animate
      >
        <div className="section-container anatomy-container">
          {/* Header */}
          <div className="anatomy-header">
            <h2>See how the Blueprint works</h2>
            <p className="anatomy-subtitle">One page per topic. Clear actions. Clear checkpoints. Less guesswork.</p>
          </div>
          
          {/* Hero Visual - Anatomy of a Page */}
          <div className="anatomy-hero">
            {/* Ambient glow behind */}
            <div className="anatomy-ambient-glow"></div>
            
            {/* The floating page mockup */}
            <div className="anatomy-page-container">
              <div className="anatomy-page">
                {/* Page header */}
                <div className="anatomy-page-header">
                  <span className="anatomy-page-module">MODULE 4</span>
                  <h3 className="anatomy-page-title">Photo Privacy</h3>
                  <p className="anatomy-page-intro">Control who sees your images and what data they carry.</p>
                </div>
                
                {/* Section: Why this matters */}
                <div className="anatomy-page-section section-why" data-callout="why">
                  <div className="anatomy-section-marker"></div>
                  <h4>Why this matters</h4>
                  <p>Photos contain hidden metadata — location, time, device info. When shared publicly, this data follows them.</p>
                </div>
                
                {/* Section: Do this */}
                <div className="anatomy-page-section section-do" data-callout="do">
                  <div className="anatomy-section-marker"></div>
                  <h4>Do this</h4>
                  <ul className="anatomy-checklist">
                    <li><span className="anatomy-checkbox"></span>Review photo album privacy on Facebook and Instagram</li>
                    <li><span className="anatomy-checkbox"></span>Disable location tagging in your camera settings</li>
                    <li><span className="anatomy-checkbox"></span>Check which apps can access your photo library</li>
                  </ul>
                </div>
                
                {/* Section: Done when */}
                <div className="anatomy-page-section section-done" data-callout="done">
                  <div className="anatomy-section-marker"></div>
                  <h4>Done when</h4>
                  <div className="anatomy-done-check">
                    <Check size={16} />
                    <span>Your photos are only visible to people you've chosen, and new photos don't auto-share location.</span>
                  </div>
                </div>
                
                {/* Section: Habits to keep */}
                <div className="anatomy-page-section section-habits" data-callout="habits">
                  <div className="anatomy-section-marker"></div>
                  <h4>Habits to keep</h4>
                  <p>Review tagged photos monthly. Audit app photo access quarterly.</p>
                </div>
                
                {/* Section: If you only do one thing */}
                <div className="anatomy-page-section section-one" data-callout="one">
                  <div className="anatomy-section-marker gold"></div>
                  <h4>If you only do one thing</h4>
                  <p className="anatomy-highlight">Turn off location data in your camera app settings.</p>
                </div>
                
                {/* Page footer */}
                <div className="anatomy-page-footer">
                  <span className="anatomy-page-number">PAGE 12</span>
                </div>
              </div>
              
              {/* Callout annotations */}
              <div className="anatomy-callouts">
                <div className="anatomy-callout callout-why">
                  <div className="callout-line"></div>
                  <div className="callout-content">
                    <span className="callout-label">Context</span>
                    <span className="callout-desc">Understand the risk before acting</span>
                  </div>
                </div>
                
                <div className="anatomy-callout callout-do">
                  <div className="callout-line"></div>
                  <div className="callout-content">
                    <span className="callout-label">Action</span>
                    <span className="callout-desc">Exactly what to change, step by step</span>
                  </div>
                </div>
                
                <div className="anatomy-callout callout-done">
                  <div className="callout-line"></div>
                  <div className="callout-content">
                    <span className="callout-label">Checkpoint</span>
                    <span className="callout-desc">How to know it's actually done</span>
                  </div>
                </div>
                
                <div className="anatomy-callout callout-habits">
                  <div className="callout-line"></div>
                  <div className="callout-content">
                    <span className="callout-label">Maintenance</span>
                    <span className="callout-desc">Simple habits to stay protected</span>
                  </div>
                </div>
                
                <div className="anatomy-callout callout-one">
                  <div className="callout-line gold"></div>
                  <div className="callout-content">
                    <span className="callout-label gold">Priority</span>
                    <span className="callout-desc">The one action with the biggest impact</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Bottom value strip */}
          <div className="anatomy-value-strip">
            <div className="anatomy-value">
              <FileText size={18} />
              <span>One page per topic</span>
            </div>
            <div className="anatomy-value">
              <CheckCircle size={18} />
              <span>Clear actions</span>
            </div>
            <div className="anatomy-value">
              <Check size={18} />
              <span>Done-when checkpoints</span>
            </div>
            <div className="anatomy-value">
              <Sparkles size={18} />
              <span>Less guesswork</span>
            </div>
          </div>
        </div>
      </section>

      {/* Built for Real Life (renamed) */}
      <section className="blueprint-section blueprint-audience" id="audience" data-animate>
        <div className="section-container">
          <h2>Built for real life</h2>
          
          <div className="audience-list">
            <div className="audience-item">
              <CheckCircle size={20} />
              <span>You want to feel confident, not confused</span>
            </div>
            <div className="audience-item">
              <CheckCircle size={20} />
              <span>You don't want to spend hours researching</span>
            </div>
            <div className="audience-item">
              <CheckCircle size={20} />
              <span>You want a calm plan you can follow today</span>
            </div>
            <div className="audience-item">
              <CheckCircle size={20} />
              <span>You're protecting yourself and your family — without turning life into IT support</span>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Table Section - Main Pricing */}
      <section className="blueprint-section blueprint-comparison" id="pricing" data-animate>
        <div className="section-container">
          <h2>Choose your pace</h2>
          <p className="section-intro">Choose the format that matches how you like to learn.</p>
          
          <div className="comparison-table">
            {/* PDF Blueprint Tile - $28 */}
            <div 
              className="comparison-column clickable-tile"
              role="button"
              tabIndex={0}
              aria-label="Get PDF Blueprint for $28 AUD - Self-guided checklist"
              onClick={(e) => {
                // Prevent if already processing
                if (loading) return;
                // Add pressed state
                e.currentTarget.classList.add('pressed');
                // Delay navigation to show feedback
                setTimeout(() => handleCheckout('pdf'), 150);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  if (loading) return;
                  e.currentTarget.classList.add('pressed');
                  setTimeout(() => handleCheckout('pdf'), 150);
                }
              }}
              onPointerDown={(e) => {
                e.currentTarget.classList.add('pressed');
              }}
              onPointerUp={(e) => {
                e.currentTarget.classList.remove('pressed');
              }}
              onPointerLeave={(e) => {
                e.currentTarget.classList.remove('pressed');
              }}
              data-testid="pricing-tile-pdf"
            >
              <div className="tile-glow-effect"></div>
              <div className="tile-sheen"></div>
              <div className="comparison-header">
                <FileText size={24} />
                <h3>PDF Blueprint</h3>
                <span className="comparison-price">$28 AUD</span>
                <span className="comparison-badge">Self-guided</span>
              </div>
              <div className="comparison-rows">
                <div className="comparison-row">
                  <span className="comparison-label">Format</span>
                  <span className="comparison-value">PDF checklist (one page per module)</span>
                </div>
                <div className="comparison-row">
                  <span className="comparison-label">Guidance</span>
                  <span className="comparison-value">You follow the checklist and apply the settings on your own device.</span>
                </div>
                <div className="comparison-row">
                  <span className="comparison-label">Includes</span>
                  <span className="comparison-value">Steps + checkpoints ("done when…") for each module</span>
                </div>
                <div className="comparison-row">
                  <span className="comparison-label">Best for</span>
                  <span className="comparison-value">I want quick, self-paced actions</span>
                </div>
                <div className="comparison-row">
                  <span className="comparison-label">Delivery</span>
                  <span className="comparison-value">PDF delivered via email</span>
                </div>
              </div>
              <span className="comparison-cta-visual">
                Get PDF Blueprint — $28
              </span>
            </div>
            
            {/* Guided Videos Tile - $88 */}
            <div 
              className="comparison-column featured clickable-tile"
              role="button"
              tabIndex={0}
              aria-label="Get Guided Videos and Blueprint for $88 AUD - Step-by-step video guidance"
              onClick={(e) => {
                if (loading) return;
                e.currentTarget.classList.add('pressed');
                setTimeout(() => handleCheckout('videos'), 150);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  if (loading) return;
                  e.currentTarget.classList.add('pressed');
                  setTimeout(() => handleCheckout('videos'), 150);
                }
              }}
              onPointerDown={(e) => {
                e.currentTarget.classList.add('pressed');
              }}
              onPointerUp={(e) => {
                e.currentTarget.classList.remove('pressed');
              }}
              onPointerLeave={(e) => {
                e.currentTarget.classList.remove('pressed');
              }}
              data-testid="pricing-tile-videos"
            >
              <div className="tile-glow-effect"></div>
              <div className="tile-sheen"></div>
              <div className="comparison-header">
                <PlayCircle size={24} />
                <h3>Guided Videos + Blueprint</h3>
                <span className="comparison-price">$88 AUD</span>
                <span className="comparison-badge guided">Guided</span>
              </div>
              <div className="comparison-rows">
                <div className="comparison-row">
                  <span className="comparison-label">Format</span>
                  <span className="comparison-value">Video walkthrough + PDF Blueprint</span>
                </div>
                <div className="comparison-row">
                  <span className="comparison-label">Guidance</span>
                  <span className="comparison-value">We show you the exact screens, where to tap, and what to change — step by step.</span>
                </div>
                <div className="comparison-row">
                  <span className="comparison-label">Includes</span>
                  <span className="comparison-value">Everything in the Blueprint + on-screen walkthroughs and examples</span>
                </div>
                <div className="comparison-row">
                  <span className="comparison-label">Best for</span>
                  <span className="comparison-value">I want confidence I'm doing it correctly (and fast)</span>
                </div>
                <div className="comparison-row">
                  <span className="comparison-label">Delivery</span>
                  <span className="comparison-value">Instant course access (videos) + PDF included</span>
                </div>
              </div>
              <span className="comparison-cta-visual featured">
                Get Guided Videos — $88
              </span>
            </div>
          </div>
          
          <p className="pricing-bridge">
            <Sparkles size={14} />
            Start with the PDF — upgrade later and your $28 is credited.
          </p>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="blueprint-section blueprint-faq" id="faq" data-animate>
        <div className="section-container">
          <h2>Questions? We've got answers.</h2>
          
          <div className="faq-list">
            {faqs.map((faq, index) => (
              <div 
                key={index} 
                className={`faq-item ${expandedFaq === index ? 'expanded' : ''}`}
              >
                <button 
                  className="faq-question"
                  onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                  aria-expanded={expandedFaq === index}
                >
                  <span>{faq.q}</span>
                  {expandedFaq === index ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </button>
                <div className="faq-answer">
                  <p>{faq.a}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="blueprint-section blueprint-final-cta">
        <div className="section-container">
          <h2>Ready to feel more secure online?</h2>
          <p>Get your Blueprint and start making progress today.</p>
          
          <div className="final-cta-buttons">
            <button 
              className="blueprint-cta-primary"
              onClick={() => handleCheckout('pdf')}
              disabled={loading}
            >
              Get PDF Blueprint — $28
              <ArrowRight size={20} />
            </button>
            <button 
              className="blueprint-cta-secondary"
              onClick={() => handleCheckout('videos')}
              disabled={loading}
            >
              Prefer videos? Get both — $88
            </button>
          </div>
          
          <div className="final-stripe-note">
            <Lock size={14} /> Secure checkout with Stripe • All prices in AUD
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="blueprint-footer">
        <div className="footer-links">
          <Link to="/privacy-policy">Privacy Policy</Link>
          <Link to="/terms-of-use">Terms</Link>
          <Link to="/contact">Contact</Link>
        </div>
        <p className="footer-note">All prices in AUD. Secure payment via Stripe.</p>
        <p className="footer-copyright">© 2025 All Secured. All rights reserved.</p>
      </footer>

      {/* Sticky Mobile CTA Bar */}
      <div className={`blueprint-sticky-bar ${showStickyBar ? 'visible' : ''}`}>
        <button 
          className="sticky-cta-primary"
          onClick={() => handleCheckout('pdf')}
          disabled={loading}
        >
          Get PDF — $28
        </button>
        <button 
          className="sticky-cta-secondary"
          onClick={() => handleCheckout('videos')}
          disabled={loading}
        >
          Videos — $88
        </button>
      </div>

      {/* Video Modal */}
      {showVideoModal && (
        <div className="video-modal-overlay" onClick={closeVideoModal}>
          <div className="video-modal" onClick={e => e.stopPropagation()}>
            <button className="video-modal-close" onClick={closeVideoModal}>
              <X size={24} />
            </button>
            <div className="video-modal-content">
              <iframe
                src={`https://player.vimeo.com/video/${VIMEO_VIDEO_ID}?autoplay=1&muted=0&badge=0&autopause=0&player_id=0&app_id=58479&texttrack=false`}
                className="video-modal-iframe"
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
                title="Course walkthrough - Full"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlueprintLanding;
